import { hasVoiceProviderSupport } from '@/constants/voiceProviderLanguageMap';
import { BrainLanguage } from '@/shared/types/brain';

/** Language codes pinned to the top of language picker dropdowns. */
export const PRIORITY_LANGUAGES = ['en', 'pt-br', 'es', 'el'];

/** Native and English display names for each supported language code. */
const languageDisplayNames: Record<string, { native: string; english: string }> = {
  ar: { native: 'العربية', english: 'Arabic' },
  bg: { native: 'Български', english: 'Bulgarian' },
  cs: { native: 'Čeština', english: 'Czech' },
  cy: { native: 'Cymraeg', english: 'Welsh' },
  da: { native: 'Dansk', english: 'Danish' },
  de: { native: 'Deutsch', english: 'German' },
  el: { native: 'Ελληνικά', english: 'Greek' },
  en: { native: 'English', english: 'English' },
  es: { native: 'Español', english: 'Spanish' },
  fi: { native: 'Suomi', english: 'Finnish' },
  fr: { native: 'Français', english: 'French' },
  he: { native: 'עברית', english: 'Hebrew' },
  hr: { native: 'Hrvatski', english: 'Croatian' },
  id: { native: 'Bahasa Indonesia', english: 'Indonesian' },
  it: { native: 'Italiano', english: 'Italian' },
  ja: { native: '日本語', english: 'Japanese' },
  me: { native: 'Crnogorski', english: 'Montenegrin' },
  ms: { native: 'Bahasa Melayu', english: 'Malay' },
  nl: { native: 'Nederlands', english: 'Dutch' },
  'nl-be': { native: 'Belgisch Nederlands', english: 'Dutch' },
  no: { native: 'Norsk', english: 'Norwegian' },
  pl: { native: 'Polski', english: 'Polish' },
  pt: { native: 'Português', english: 'Portuguese' },
  'pt-br': { native: 'Português Brasileiro', english: 'Portuguese' },
  ro: { native: 'Română', english: 'Romanian' },
  ru: { native: 'Русский', english: 'Russian' },
  sq: { native: 'Shqip', english: 'Albanian' },
  sr: { native: 'Српски', english: 'Serbian' },
  sv: { native: 'Svenska', english: 'Swedish' },
  th: { native: 'ไทย', english: 'Thai' },
  tl: { native: 'Tagalog', english: 'Tagalog' },
  tr: { native: 'Türkçe', english: 'Turkish' },
  vi: { native: 'Tiếng Việt', english: 'Vietnamese' },
  zh: { native: '中文', english: 'Chinese' },
};

/** Places priority languages first, then sorts the rest alphabetically by English name. */
export const prioritizeLanguageOptions = <T extends { value: string; label: string }>(
  options: T[]
): T[] => {
  const prioritySet = new Set(PRIORITY_LANGUAGES);
  const top = PRIORITY_LANGUAGES.map((code) =>
    options.find((o) => o.value.toLowerCase() === code)
  ).filter(Boolean) as T[];
  const rest = options
    .filter((o) => !prioritySet.has(o.value.toLowerCase()))
    .sort((a, b) => {
      const englishA = languageDisplayNames[a.value.toLowerCase()]?.english ?? a.label;
      const englishB = languageDisplayNames[b.value.toLowerCase()]?.english ?? b.label;
      return englishA.localeCompare(englishB);
    });
  return [...top, ...rest];
};

/** Returns the display label for a language code, falls back to the raw code. */
export const formatLanguageLabel = (code: string): string => {
  const data = languageDisplayNames[code.toLowerCase()];
  if (!data) {
    return code;
  }
  return data.native === data.english ? data.english : `${data.native} (${data.english})`;
};

/**
 * Builds the language options list for voice integrations — BrainLanguage values
 * filtered by voice provider support, with the currently-selected language pinned
 * first and the rest sorted alphabetically by display label.
 */
export const buildVoiceLanguageOptions = (
  languageValue: string
): { value: string; label: string }[] => {
  return Object.values(BrainLanguage)
    .filter(hasVoiceProviderSupport)
    .map((code) => ({ value: code, label: formatLanguageLabel(code) }))
    .sort((a, b) => {
      if (a.value === languageValue && b.value !== languageValue) {
        return -1;
      }
      if (b.value === languageValue && a.value !== languageValue) {
        return 1;
      }
      return a.label.localeCompare(b.label);
    });
};
